<template>
  <div>
    <b-form-row>
      <b-col>
        <b-form-group
          id="flip-time-group"
          :state="flipTimeState"
          label="Flip Time"
          label-for="flip-time"
          description="The time at which to flip the calendar to the next day. Ex: 22:00"
          invalid-feedback="Please select a time">
          <b-input-group>
            <time-picker
              :id="id + '-flip-time'"
              required
              :minute-interval="15"
              :time.sync="flipTime"
              @update:time="onFlipTimeChange" />
            <b-input-group-append is-text>
              <span class="far fa-clock" />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-form-row>
    <b-form-row>
      <b-col>
        <b-form-group
          id="flip-time-zone-group"
          label="Flip Time Zone"
          label-for="flip-time-zone"
          description="The time zone canonical name for the flip time above. Ex: America/New_York">
          <b-form-select
            id="flip-time-zone"
            v-model="flipTimeConfig.flipTimeZone"
            required
            :options="getTimezones()" />
        </b-form-group>
      </b-col>
    </b-form-row>
    <b-form-row>
      <b-col>
        <b-form-group
          id="next-date-presented-group"
          label="Next Date Presented"
          label-for="next-date-presented"
          description="The next date to display after the flip time arrives (only applies if in the future)"
          :invalid-feedback="invalidFlipDateFeedback"
          :state="nextDatePresentedState">
          <b-input-group>
            <date-picker
              :id="id + '-next-date-presented'"
              v-model="flipTimeConfig.nextDatePresented"
              required
              :config="nextDatePickerOptions"
              @dp-change="onNextDatePresentedChanged" />
            <b-input-group-append is-text>
              <span class="far fa-calendar" />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-form-row>
    <b-form-row>
      <b-col>
        <b-form-group
          id="skip-to-next-day-after-group"
          label="Skip to Next Day After"
          label-for="skip-to-next-day-after"
          description="The date after which to apply the skip to the above date (only applies if in the future)"
          invalid-feedback="Please select a date"
          :state="skipToNextDayAfterState">
          <b-input-group>
            <date-picker
              :id="id + '-skip-to-next-day-after'"
              v-model="flipTimeConfig.skipToNextDayAfter"
              required
              :config="nextDatePickerOptions"
              @dp-change="onDateTimePickerChanged" />
            <b-input-group-append is-text>
              <span class="far fa-calendar" />
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-form-row>
  </div>
</template>

<script>
  import {mapGetters} from 'vuex'
  import TimePicker from '@/components/TimePicker'
  import DatePicker from 'vue-bootstrap-datetimepicker'
  import 'pc-bootstrap4-datetimepicker/build/css/bootstrap-datetimepicker.css'
  import moment from 'moment-timezone'
  import { StatsAPI } from '@/services/api'

  export default {
    name: "FlipTime",
    components: {
      DatePicker,
      TimePicker,
    },
    props: {
      value: {
        type: Object,
        required: true,
      },
      id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        flipTime: {
          hour: null,
          minute: null,
          second: null,
          apm: null,
        },
        nextDateHasGames: true,
      }
    },
    computed: {
      ...mapGetters([
        'isAdmin',
        'canScoreboardFlipEdit',
        'canScoreboardFlipPublish',
      ]),
      flipTimeConfig: {
        get: function() {
          return this.value
        },
        set: function (value) {
          this.$emit("input", value)
        },
      },
      nextDatePickerOptions() {
        return {
          format: 'YYYY-MM-DDTHH:mm:ssZ',
          timeZone: this.flipTimeConfig.flipTimeZone || DEFAULT_TIMEZONE,
        }
      },
      flipTimeState() {
        return this.flipTime.hour !== null &&
          this.flipTime.minute !== null
      },
      nextDatePresentedState() {
        return this.flipTimeConfig.nextDatePresented &&
          this.flipTimeConfig.nextDatePresented.length > 0 && this.nextDateHasGames
      },
      skipToNextDayAfterState() {
        return this.flipTimeConfig.skipToNextDayAfter &&
          this.flipTimeConfig.skipToNextDayAfter.length > 0
      },
      hasErrors() {
        return !this.flipTimeState ||
          !this.nextDatePresentedState ||
          !this.skipToNextDayAfterState
      },
      invalidFlipDateFeedback() {
        return !this.nextDateHasGames ? 'Selected date has no scheduled games' : 'Please select a valid date'
      },
    },
    watch: {
      hasErrors(newVal) {
        this.$emit("has-errors", newVal)
      },
    },
    created() {
      if (this.flipTimeConfig.flipTime)  
        this.parseFlipTime(this.flipTimeConfig.flipTime)
    },
    methods: {
      parseFlipTime(flipTime) {
        const colonIndex = flipTime.indexOf(':')

        if (colonIndex > 0) {
          this.flipTime = {
            hour: flipTime.substring(0, colonIndex),
            minute: flipTime.substring(colonIndex + 1, flipTime.length),
          }
        } else {
          console.warn(`Beta config flip time (${flipTime}) was improperly formatted, setting local value to default.`)
        }
      },
      onCopyConfig(newVal) {
        this.flipTimeConfig.flipTime = newVal.flipTime
        this.flipTimeConfig.nextDatePresented = newVal.nextDatePresented
        this.flipTimeConfig.skipToNextDayAfter = newVal.skipToNextDayAfter
        this.flipTimeConfig.flipTimeZone = newVal.flipTimeZone
        this.parseFlipTime(this.flipTimeConfig.flipTime)
        this.$alert.success("Scoreboard flip config successfully copied to EPG flip config")
      },
      onFlipTimeChange(newVal) {
        this.flipTime = newVal
        this.flipTimeConfig.flipTime = `${newVal.hour}:${newVal.minute}`

        if (this.flipTime.hour && this.flipTime.minute) {
          const currentNextDatePresented = moment.parseZone(this.flipTimeConfig.nextDatePresented)
          const currentSkipToNextDayAfter = moment.parseZone(this.flipTimeConfig.skipToNextDayAfter)


          // Set the time for next date presented and skip to next day after to the flip time hour and minute
          this.flipTimeConfig.nextDatePresented = currentNextDatePresented.set({
            hour: this.flipTime.hour,
            minute: this.flipTime.minute,
          }).format(this.nextDatePickerOptions.format)
          this.flipTimeConfig.skipToNextDayAfter = currentSkipToNextDayAfter.set({
            hour: this.flipTime.hour,
            minute: this.flipTime.minute,
          }).format(this.nextDatePickerOptions.format)
        }
      },
      getTimezones() {
        const now = moment(Date.now())
        return moment.tz.names()
        .map(name => {
          return {
            text: `${name} (${moment.tz(now, name).format('z')})`,
            value: name,
          }
        })
      },
      onDateTimePickerChanged(newVal) {
        this.closeDateTimePicker('#' + newVal.currentTarget.id)
      },
      async onNextDatePresentedChanged(newVal) {
        const schedule = await StatsAPI.getSchedule(newVal.date)
        this.nextDateHasGames = schedule.totalGames > 0
        if (!this.nextDateHasGames) {
          this.$alert.error('The selected date has no scheduled games')
        }
        this.onDateTimePickerChanged(newVal)
      },
      closeDateTimePicker(id) {
        const dateTimePicker = this.$el.querySelector('.bootstrap-datetimepicker-widget')

        if (dateTimePicker) {
          // Programmatically collapse the date time picker if visible since we don't need time
          const inputElement = this.$el.querySelector(id)

          this.$nextTick(() => {
            if (inputElement) {
              inputElement.blur()
            }
          })
        }
      },
    },
  }
  const DEFAULT_TIMEZONE = 'America/New_York'
</script>

<style scoped>

</style>
